/** The fields of a `GET /admin/coin-packages` row this helper needs. */
export interface PackageForPicker {
  id: string;
  isActive: boolean;
  displayOrder?: number;
  amountPaise?: string | number;
}

/**
 * The packages a "pick a package" dropdown should offer: the ACTIVE ones, in
 * the same order as Admin → Coin packages and the app's store (display order,
 * then price). An inactive package is listed only when it is already selected
 * ([keepIds]) — so an old choice still shows its name instead of a raw id —
 * and goes last, for the label to flag.
 */
export function packagesForPicker<T extends PackageForPicker>(
  packages: readonly T[],
  keepIds: ReadonlyArray<string | null | undefined> = [],
): T[] {
  const keep = new Set(keepIds.filter((id): id is string => !!id));
  const byStoreOrder = (a: T, b: T) =>
    (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || Number(a.amountPaise ?? 0) - Number(b.amountPaise ?? 0);
  const active = packages.filter((p) => p.isActive).sort(byStoreOrder);
  const keptInactive = packages.filter((p) => !p.isActive && keep.has(p.id)).sort(byStoreOrder);
  return [...active, ...keptInactive];
}

/** Suffix for a kept inactive package's label. */
export const INACTIVE_PACKAGE_SUFFIX = ' — inactive, pick an active package';
